import { Button, Dropdown, Input } from '@/shared';
import { useEffect, useRef, useState } from 'react';

interface Option {
  label: string;
  value: string | number;
}

export type ResidentStatus = 'ACTIVE' | 'INACTIVE' | 'ARCHIVED';
export type PaymentStatus = 'PAID' | 'PENDING' | 'OVERDUE';
export type ResidentPaymentFrequency = 'WEEKLY' | 'MONTHLY' | 'SIXMONTHS';

export interface ResidentFiltersType {
  page: number;
  limit: number;
  search: string;
  status: ResidentStatus | '';
  paymentStatus: PaymentStatus | '';
  moveInDate: string;
  location: string;
}

interface ResidentFiltersProps {
  filters: ResidentFiltersType;
  setFilters: React.Dispatch<React.SetStateAction<ResidentFiltersType>>;
  totalResidents?: number;
  isResidentFilter?: boolean;
}

export const ResidentFiltersComponent: React.FC<ResidentFiltersProps> = ({
  filters,
  setFilters,
  totalResidents = 0,
  isResidentFilter = false,
}) => {
  const [showFilters, setShowFilters] = useState(false);
  const filterRef = useRef<HTMLDivElement>(null);

  // === OPTIONS ===
  const locationOptions: Option[] = [
    { label: 'Karachi', value: 'karachi' },
    { label: 'Lahore', value: 'lahore' },
    { label: 'Islamabad', value: 'islamabad' },
  ];

  const paymentStatusOptions: Option[] = [
    { label: 'Paid', value: 'PAID' },
    { label: 'Pending', value: 'PENDING' },
    { label: 'Overdue', value: 'OVERDUE' },
  ];

  // === HANDLERS ===
  const handleSelect = (
    field: keyof ResidentFiltersType,
    value: string | number,
  ) => {
    setFilters((prev) => ({
      ...prev,
      [field]: value,
      page: 1,
    }));
  };

  const handleStatusClick = (status: ResidentStatus) => {
    setFilters((prev) => ({
      ...prev,
      status,
      page: 1,
    }));
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const searchValue = e.target.value;
    setFilters((prev) => ({
      ...prev,
      search: searchValue,
      page: 1,
    }));
  };

  const clearFilterHandler = () => {
    setFilters({
      page: 1,
      limit: 10,
      search: '',
      status: isResidentFilter ? 'ARCHIVED' : 'ACTIVE',
      paymentStatus: '',
      moveInDate: '',
      location: '',
    });
  };

  // === OUTSIDE CLICK HANDLER ===
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        filterRef.current &&
        !filterRef.current.contains(event.target as Node)
      ) {
        setShowFilters(false);
      }
    };

    if (showFilters) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showFilters]);

  // === RENDER ===
  return (
    <div className="w-full">
      <div className="flex flex-col lg:flex-row items-center justify-between">
        {/* Title */}
        <div className="mb-4">
          <p className="text-lg md:text-2xl text-text-primary font-bold">
            All Residents
            <span className="font-normal text-xl"> ({totalResidents})</span>
          </p>
        </div>

        {/* Filters */}
        <div className="w-full md:w-auto flex flex-col md:flex-row items-center gap-4">
          {/* Search */}
          <div className="flex items-center w-full md:w-[256px]">
            <Input
              className="rounded-lg !w-full !text-[16px]"
              placeholder="Search residents"
              value={filters.search}
              onChange={handleSearchChange}
            />
          </div>

          {/* Filter Button */}
          <div className="relative w-full md:w-auto" ref={filterRef}>
            <div
              className="w-full flex items-center justify-end md:justify-start gap-1 cursor-pointer card p-3 text-text-secondary"
              onClick={() => setShowFilters((prev) => !prev)}
            >
              <svg
                width="18"
                height="12"
                viewBox="0 0 18 12"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M7 12V10H11V12H7ZM3 7V5H15V7H3ZM0 2V0H18V2H0Z"
                  fill="#474747"
                />
              </svg>
              <span>Filter</span>
            </div>

            {/* Filter Panel */}
            {showFilters && (
              <div className="absolute top-12 right-0 z-50 shadow-xl shadow-gray-300">
                <div className="card md:min-w-120 p-4">
                  {/* Status */}
                  <div className="flex flex-col sm:flex-row items-center justify-between">
                    <span className="text-base md:text-xl text-text-primary">Status</span>
                    <div className="flex gap-2">
                      {['ACTIVE', 'INACTIVE', 'ARCHIVED'].map((status) => (
                        <button
                          key={status}
                          onClick={() =>
                            handleStatusClick(status as ResidentStatus)
                          }
                          className={`rounded-full px-2 py-1 text-sm ${
                            filters.status === status
                              ? 'bg-black text-white'
                              : 'text-text-secondary'
                          }`}
                        >
                          {status}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Payment Status */}
                  <FilterRow
                    label="Payment Status"
                    options={paymentStatusOptions}
                    value={filters.paymentStatus}
                    onChange={(val) =>
                      handleSelect('paymentStatus', val as string)
                    }
                  />

                  {/* Location */}
                  <FilterRow
                    label="Location"
                    options={locationOptions}
                    value={filters.location}
                    onChange={(val) => handleSelect('location', val as string)}
                  />

                  {/* Move-In Date */}
                  <div className="flex items-center justify-between">
                    <span className="text-base md:text-xl text-text-primary w-[50%]">
                      Move-In Date
                    </span>
                    <input
                      type="date"
                      value={filters.moveInDate}
                      onChange={(e) =>
                        handleSelect('moveInDate', e.target.value)
                      }
                      className="!w-[50%] py-2"
                    />
                  </div>

                  {/* Clear Filters */}
                  <div className="flex justify-end">
                    <Button onClick={clearFilterHandler}>Clear Filters</Button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// ================== REUSABLE ROW COMPONENT ==================
interface FilterRowProps {
  label: string;
  options: Option[];
  value: string | number;
  onChange: (val: unknown) => void;
}

const FilterRow: React.FC<FilterRowProps> = ({
  label,
  options,
  value,
  onChange,
}) => (
  <div className="flex items-center justify-between">
    <span className="text-base lg:text-xl text-text-primary">{label}</span>
    <Dropdown
      options={options}
      value={value}
      placeholder={`Select ${label.toLowerCase()}`}
      onChange={onChange}
      className="!w-auto"
      buttonClassName="!min-w-fit !gap-4 text-text-secondary !border-[0] !p-3"
    />
  </div>
);
